/**
 * 会社の設定の値を、画面の言葉と入力の形に行き来させる（issue #124）。
 *
 * 値の型・保存してよいかの判定の正はサーバ（`server/src/settings/`）。ここは見せ方だけで、
 * 保存するときはサーバの答え（断られたら理由）をそのまま画面に出します。
 */
import type { SettingItem } from '../api/types.js';
import { channelTypeLabel, readReceiptLabel } from '../channels/labels.js';

export const MEGABYTE = 1024 * 1024;

/** トークの種類ごとの決まり（設定の表の書き方）。画面で変えない項目もそのまま持ち回る */
export interface PresetFields {
  post_policy: 'members' | 'admins';
  read_receipt_display: 'count' | 'none';
  notify_with_body: boolean;
  [field: string]: unknown;
}
export type PresetTable = Record<string, PresetFields>;
export type RetentionTable = Record<string, number | null>;
export type AllowedTypes = Record<string, string[]>;
/** 拠点のひな形の1行（作るトークの種類と名前・issue #517） */
export interface SiteTemplateRow {
  type: string;
  name: string;
}

/** 入力中の値（欄ごとの形）。 */
export type SettingDraft =
  | { kind: 'text'; text: string }
  | { kind: 'switch'; on: boolean }
  | { kind: 'presets'; table: PresetTable }
  | { kind: 'retention'; rows: Record<string, { days: string; unlimited: boolean }> }
  | { kind: 'types'; checked: string[] }
  | { kind: 'template'; rows: SiteTemplateRow[] };

/** 拠点のひな形に入れられる種類（サーバの AUTO_JOIN_CHANNEL_TYPES と同じ。名簿に合わせて人が入る種類） */
export const SITE_TEMPLATE_TYPES = ['official', 'announcement'] as const;
/** 拠点のひな形に入れられるトークの数（サーバの SITE_CHANNEL_TEMPLATE_MAX と同じ） */
export const SITE_TEMPLATE_MAX = 5;
/** 拠点のひな形の名前の長さ（サーバの SITE_CHANNEL_TEMPLATE_NAME_MAX と同じ） */
export const SITE_TEMPLATE_NAME_MAX = 40;

/** 拠点のひな形を1行の言葉にする（例: 公式「{拠点名}の連絡」）。 */
export function siteTemplateRowText(row: SiteTemplateRow): string {
  return `${channelTypeLabel(row.type)}「${row.name}」`;
}

/** 「メッセージを送れる方」を選べない種類（1対1・AI先輩に教える。サーバも断る） */
export const FIXED_POST_POLICY_TYPES = new Set(['dm', 'teach']);

/** 大きさの言い方（1GB 以上は GB、それ未満は MB）。 */
export function bytesText(bytes: number): string {
  const gb = 1024 * MEGABYTE;
  if (bytes >= gb) {
    const value = bytes / gb;
    return `${Number.isInteger(value) ? value : value.toFixed(1)}GB`;
  }
  return `${Math.round(bytes / MEGABYTE).toLocaleString('ja-JP')}MB`;
}

/** 送れるファイルの形式の呼び名（例: 写真（.jpg .jpeg））。 */
export function attachmentTypeLabel(contentType: string, extensions: readonly string[]): string {
  const dotted = extensions.map((ext) => `.${ext}`).join(' ');
  if (contentType.startsWith('image/')) return `写真（${dotted}）`;
  if (contentType.startsWith('video/')) return `動画（${dotted}）`;
  if (contentType === 'application/pdf') return `PDF（${dotted}）`;
  if (contentType.includes('wordprocessingml')) return `Word（${dotted}）`;
  if (contentType.includes('spreadsheetml')) return `Excel（${dotted}）`;
  if (contentType.includes('presentationml')) return `PowerPoint（${dotted}）`;
  return `ファイル（${dotted}）`;
}

function daysText(days: number | null | undefined): string {
  return days === null || days === undefined ? '無期限' : `${days.toLocaleString('ja-JP')}日`;
}

/** 種類の並び（はじめから用意してある種類を先に、足した種類はそのあとに名前の順）。 */
const TYPE_ORDER = ['official', 'group', 'dm', 'announcement', 'teach'];

export function sortedTypes(types: Iterable<string>): string[] {
  const all = [...new Set(types)];
  return all.sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a);
    const ib = TYPE_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
}

/** 1つの種類の決まりを1行の言葉にする。 */
export function presetText(type: string, preset: PresetFields | undefined): string {
  if (!preset) return `${channelTypeLabel(type)}：（決まりなし）`;
  return `${channelTypeLabel(type)}：${[
    preset.post_policy === 'admins' ? '管理者だけが送れる' : '参加している方が送れる',
    readReceiptLabel(preset.read_receipt_display),
    preset.notify_with_body ? '通知に本文を載せる' : '通知に本文を載せない',
  ].join('・')}`;
}

/** 使う／使わないの設定の言い方（「〜を使う」の設定は「使う」、ほかは「する」）。 */
export function switchWord(label: string, on: boolean): string {
  if (label.endsWith('使う')) return on ? '使う' : '使わない';
  return on ? 'する' : 'しない';
}

/** 値を画面の言葉にする（一覧の表示・確認の窓の「変更前 → 変更後」）。 */
export function settingValueText(
  setting: Pick<SettingItem, 'editor' | 'unit' | 'label'>,
  value: unknown,
): string {
  switch (setting.editor) {
    case 'number':
      return typeof value === 'number'
        ? `${value.toLocaleString('ja-JP')}${setting.unit ?? ''}`
        : String(value);
    case 'megabytes':
      return typeof value === 'number' ? bytesText(value) : String(value);
    case 'switch':
      return switchWord(setting.label, value === true);
    case 'url':
      return typeof value === 'string' && value !== '' ? value : '（空欄。メニューに出しません）';
    case 'text':
    case 'long_text':
    case 'time':
      return typeof value === 'string' && value !== '' ? value : '（空欄）';
    case 'channel_types': {
      const table = (value ?? {}) as PresetTable;
      return sortedTypes(Object.keys(table))
        .map((type) => presetText(type, table[type]))
        .join('\n');
    }
    case 'retention_by_type': {
      const table = (value ?? {}) as RetentionTable;
      return sortedTypes(Object.keys(table))
        .map((type) => `${channelTypeLabel(type)} ${daysText(table[type])}`)
        .join('・');
    }
    case 'attachment_types': {
      const table = (value ?? {}) as AllowedTypes;
      return Object.values(table)
        .flat()
        .map((ext) => `.${ext}`)
        .join(' ');
    }
    case 'site_channel_template': {
      const rows = Array.isArray(value) ? (value as SiteTemplateRow[]) : [];
      return rows.length === 0 ? '作らない' : rows.map(siteTemplateRowText).join('・');
    }
  }
}

/** 値から、入力中の形を作る。 */
export function toDraft(setting: SettingItem, value: unknown, types: string[]): SettingDraft {
  switch (setting.editor) {
    case 'number':
      return { kind: 'text', text: typeof value === 'number' ? String(value) : '' };
    case 'megabytes':
      return {
        kind: 'text',
        text: typeof value === 'number' ? String(Math.round(value / MEGABYTE)) : '',
      };
    case 'switch':
      return { kind: 'switch', on: value === true };
    case 'text':
    case 'long_text':
    case 'time':
    case 'url':
      return { kind: 'text', text: typeof value === 'string' ? value : '' };
    case 'channel_types':
      return { kind: 'presets', table: JSON.parse(JSON.stringify(value ?? {})) as PresetTable };
    case 'retention_by_type': {
      const table = (value ?? {}) as RetentionTable;
      const rows: Record<string, { days: string; unlimited: boolean }> = {};
      for (const type of sortedTypes([...types, ...Object.keys(table)])) {
        const days = table[type];
        rows[type] =
          days === null || days === undefined
            ? { days: '', unlimited: true }
            : { days: String(days), unlimited: false };
      }
      return { kind: 'retention', rows };
    }
    case 'attachment_types':
      return { kind: 'types', checked: Object.keys((value ?? {}) as AllowedTypes) };
    case 'site_channel_template':
      return {
        kind: 'template',
        rows: (Array.isArray(value) ? (value as SiteTemplateRow[]) : []).map((row) => ({
          type: row.type,
          name: row.name,
        })),
      };
  }
}

/** 整数として読めるときだけ数にする（読めないときは入れた文字のまま送り、サーバの理由を出す）。 */
function integerOrRaw(text: string): number | string {
  const trimmed = text.trim();
  return /^-?\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
}

/** 入力中の形から、サーバへ送る値を作る。 */
export function fromDraft(setting: SettingItem, draft: SettingDraft): unknown {
  switch (draft.kind) {
    case 'text': {
      if (setting.editor === 'number') return integerOrRaw(draft.text);
      if (setting.editor === 'megabytes') {
        const mb = integerOrRaw(draft.text);
        return typeof mb === 'number' ? mb * MEGABYTE : mb;
      }
      return draft.text;
    }
    case 'switch':
      return draft.on;
    case 'presets':
      return draft.table;
    case 'retention': {
      const table: Record<string, number | string | null> = {};
      for (const [type, row] of Object.entries(draft.rows)) {
        table[type] = row.unlimited ? null : integerOrRaw(row.days);
      }
      return table;
    }
    case 'template':
      // 名前の前後の空白はサーバが除く（空欄・長すぎる名前は、サーバが理由つきで断る）
      return draft.rows.map((row) => ({ type: row.type, name: row.name }));
    case 'types': {
      // 選べる形式はサーバの既定値の一覧（拡張子もそこから）
      const all = (setting.default_value ?? {}) as AllowedTypes;
      const current = (setting.value ?? {}) as AllowedTypes;
      const table: AllowedTypes = {};
      for (const contentType of Object.keys(all)) {
        if (draft.checked.includes(contentType)) {
          table[contentType] = current[contentType] ?? all[contentType] ?? [];
        }
      }
      return table;
    }
  }
}

/** 値を比べるための書き出し（キーの順番に左右されない）。 */
export function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (typeof value === 'object' && value !== null) {
    return `{${Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical((value as Record<string, unknown>)[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * 種類ごとの保存日数を比べるときは、書いていない種類を「無期限」とみなす
 * （サーバの retentionDaysForChannelType と同じ読み方）。
 */
export function sameSettingValue(setting: SettingItem, a: unknown, b: unknown): boolean {
  if (setting.editor === 'retention_by_type') {
    const x = (a ?? {}) as Record<string, unknown>;
    const y = (b ?? {}) as Record<string, unknown>;
    const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
    for (const key of keys) {
      if (canonical(x[key] ?? null) !== canonical(y[key] ?? null)) return false;
    }
    return true;
  }
  return canonical(a) === canonical(b);
}

/** 画面で「メッセージを送れる方」を選べる種類か。 */
export function canChoosePostPolicy(type: string): boolean {
  return !FIXED_POST_POLICY_TYPES.has(type);
}
